'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/dialog/styles.css';
import { type DialogSize, toDialogDepth } from 'BomdisoftComponents/dialog/render';
import { closeDialog, getDialogDepth, openDialog, subscribeToDialogStack } from 'BomdisoftComponents/dialog/stack';
import { BottomSheet } from 'BomdisoftComponents/drawers/bottom-sheet';
import { useMediaQuery } from 'BomdisoftComponents/hooks/use-media-query';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { MOBILE_BREAKPOINT } from 'BomdisoftComponents/layout/style';
import { createContext, type ReactNode, useContext, useEffect, useId, useMemo, useRef, useSyncExternalStore } from 'react';

/** Below the mobile breakpoint a `form` dialog is a bottom sheet. The same
 *  width the nav becomes a bar and a dialog already goes to 92vw, and the one
 *  every console's own narrow hook answers, so a run detail, a member panel
 *  and a form change shape together. */
const SHEET_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

/* The title's id, handed down rather than passed: `DialogTitle` is rendered as
   a child with no reference back to the dialog, and the dialog (or the sheet
   it becomes) has to point `aria-labelledby` at whatever heading it holds. */
const TitleIdContext = createContext<string | undefined>(undefined);

export type { DialogSize } from 'BomdisoftComponents/dialog/render';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  dismissible?: boolean;
  /** The title bar is the drag handle (user rule, 2026-08-18): every dialog
   * moves by its header, the cursor showing the move variant there. Opt out
   * only for a dialog that must stay anchored. */
  draggable?: boolean;
  /** This dialog holds fields the reader types into. Below the mobile
   * breakpoint it rises as the shared bottom sheet, where a phone's keyboard
   * leaves room for it and the actions stay reachable; a dialog that only
   * informs or asks for a decision stays a dialog (user rule, 2026-09-20).
   * `PromptDialog` is always one; `ConfirmDialog` is one when it asks for a
   * typed phrase. */
  form?: boolean;
  /** `false` renders via `show()` instead of `showModal()`: no backdrop, the
   * page and other dialogs stay interactive, so dialogs can stack live (user
   * rule, 2026-08-18). Escape does not close a non-modal dialog; give it an
   * explicit close control. */
  modal?: boolean;
  size?: DialogSize;
  testId?: string;
};

export const Dialog = ({ open, onClose, children, className, dismissible = true, draggable = true, form = false, modal = true, size = 'default', testId }: DialogProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useMemo(() => Symbol('dialog'), []);
  const titleId = useId();
  // The server answers the dialog, as every narrow hook in the console does:
  // nothing is a sheet in the first HTML, and a phone corrects it on hydration
  // before anything is open.
  const sheet = useMediaQuery(SHEET_QUERY) && form;

  // Registered on open and released on close, so depth follows the order
  // dialogs actually appeared in rather than where they sit in the tree.
  useEffect(() => {
    if (!open) return undefined;
    openDialog(id);
    return () => closeDialog(id);
  }, [id, open]);

  const depth = useSyncExternalStore(
    subscribeToDialogStack,
    () => getDialogDepth(id),
    // Nothing is open during a server render, so every dialog is a ground floor
    // one until it mounts. Returning the live depth here would differ between
    // server and client and React would discard the markup.
    () => -1,
  );

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (modal) dialog.showModal();
      else dialog.show();
    }
    if (!open && dialog.open) {
      dialog.close();
      // The next open starts centred again, not where the last drag left it.
      dialog.style.translate = '';
    }
  }, [modal, open]);

  // Dragging lives on the dialog and listens for the title, because the title
  // is a separate component with no ref back here: the handle is whatever
  // `DialogTitle` rendered (`h2`), found by its class at pointer-down time.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !draggable) return;

    let startX = 0;
    let startY = 0;
    let baseX = 0;
    let baseY = 0;

    const handleMove = (event: globalThis.PointerEvent) => {
      dialog.style.translate = `${baseX + event.clientX - startX}px ${baseY + event.clientY - startY}px`;
    };

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };

    const handleDown = (event: globalThis.PointerEvent) => {
      const title = (event.target as Element | null)?.closest('.bomdisoft-dialog-title');
      if (!title || title.closest('dialog') !== dialog) return;
      // A control in the title bar (close, contract) is a click, not a drag.
      if ((event.target as Element).closest('button, a, input')) return;
      const [heldX = 0, heldY = 0] = dialog.style.translate ? dialog.style.translate.split(' ').map(Number.parseFloat) : [0, 0];
      baseX = heldX;
      baseY = heldY;
      startX = event.clientX;
      startY = event.clientY;
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    };

    dialog.addEventListener('pointerdown', handleDown);
    return () => {
      dialog.removeEventListener('pointerdown', handleDown);
      handleUp();
    };
  }, [draggable]);

  // Backdrop dismissal is bound here rather than through a JSX handler: a
  // <dialog> is not an interactive element, and a click handler in markup claims
  // an interactivity it does not have. There is no missing keyboard path — the
  // platform already closes a modal <dialog> on Escape, which fires onClose.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const handleBackdropClick = (event: globalThis.MouseEvent) => {
      if (dismissible && event.target === dialog) onClose();
    };

    // A modal <dialog> closes itself on Escape, and preventing the cancel event
    // is the only hook that stops it. The forced password-change dialogs must
    // not be escapable, so they opt out through `dismissible`.
    const handleCancel = (event: Event) => {
      if (!dismissible) event.preventDefault();
    };

    dialog.addEventListener('click', handleBackdropClick);
    dialog.addEventListener('cancel', handleCancel);

    return () => {
      dialog.removeEventListener('click', handleBackdropClick);
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [dismissible, onClose]);

  if (sheet) {
    return (
      <TitleIdContext.Provider value={titleId}>
        <BottomSheet className={className} dismissible={dismissible} flush labelledBy={titleId} onClose={onClose} open={open} testId={testId}>
          {children}
        </BottomSheet>
      </TitleIdContext.Provider>
    );
  }

  return (
    <TitleIdContext.Provider value={titleId}>
      <dialog
        aria-labelledby={titleId}
        className={joinClasses('bomdisoft-dialog', className)}
        data-depth={toDialogDepth(depth)}
        data-draggable={draggable ? '' : undefined}
        data-form={form ? '' : undefined}
        data-modal={modal ? undefined : 'false'}
        data-size={size}
        data-testid={testId}
        onClose={onClose}
        ref={ref}
      >
        {children}
      </dialog>
    </TitleIdContext.Provider>
  );
};

/**
 * The title bar, which is also the drag handle.
 *
 * The grip is always in the markup and hidden by CSS unless the dialog carries
 * `data-draggable`, because this component has no way to ask: it is rendered as
 * a child and holds no reference back to the dialog. Same reason the drag
 * listener finds the handle by class at pointer-down time.
 */
export const DialogTitle = ({ children, className }: { children: ReactNode; className?: string }) => {
  const titleId = useContext(TitleIdContext);
  return (
    <h2 className={joinClasses('bomdisoft-dialog-title', className)} id={titleId}>
      <svg aria-hidden='true' className='bomdisoft-dialog-grip' fill='currentColor' height='12' viewBox='0 0 5 12' width='5'>
        <rect height='12' rx='0.9' width='1.8' x='0' y='0' />
        <rect height='12' rx='0.9' width='1.8' x='3.2' y='0' />
      </svg>
      {children}
    </h2>
  );
};

export const DialogContent = ({ children, className }: { children: ReactNode; className?: string }) => <div className={joinClasses('bomdisoft-dialog-content', className)}>{children}</div>;

export const DialogActions = ({ children, className }: { children: ReactNode; className?: string }) => <div className={joinClasses('bomdisoft-dialog-actions', className)}>{children}</div>;
